import { AdornisRouter } from '@adornis/base/client/a-router';
import { EntityData } from '@adornis/baseql/entities/adornisEntity';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import '@adornis/chemistry/elements/components/x-accordeon';
import '@adornis/chemistry/elements/components/x-card';
import '@adornis/chemistry/elements/components/x-flex';
import '@adornis/chemistry/elements/components/x-icon'; //material-icon wrapper
import '@adornis/chemistry/elements/components/x-link';
import { elevation } from '@adornis/chemistry/elements/theming/design';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Decimation,
  Legend,
  LinearScale,
  LineController,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { colors } from 'client/startup';
import { EventLog } from 'db/EventLog';
import { GoalAgg } from 'db/GoalAgg';
import { MoodleAttempts } from 'db/MoodleAttempts';
import { html } from 'lit-element';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators/state.js';
import { ref } from 'lit/directives/ref.js';
import { Aggregator } from 'mingo';
import 'mingo/init/system';
import { combineLatestWith, filter, from, map, tap, throttleTime } from 'rxjs';
import { generateOptions } from './chartUtils';
import { FeedbackPop } from './feedback-pop';
import microtip from './microtip.css';
Chart.register(
  LinearScale,
  LineController,
  PointElement,
  CategoryScale,
  zoomPlugin,
  Title,
  Legend,
  Decimation,
  BarController,
  BarElement,
  Tooltip,
);

@customElement('goal-hist')
export class GoalHist extends ChemistryLitElement {
  styles() {
    return {
      '.card': { boxShadow: elevation[1], padding: '20px', gap: '20px', borderRadius: '5px' },
    };
  }

  public chartRef: HTMLCanvasElement; // Reference to the main chart, needed to initialize chartjs
  public chartMain; // Actual chart element

  @state() public usernameMapping = new LocalStorageBehaviorController(this, 'usernameMapping', [] as string[][]);
  @state() public courseSelected = new ObservableController(this, AdornisRouter.get().queryParams.get('course'));

  @state() public updating = new BehaviorController(this, false);
  @state() public lastUpdate = new ObservableController(
    this,
    EventLog.lastUpdate(this.courseSelected.value)(EventLog.allFields),
  );
  public rawTasks = new DataLoadingController(this, () =>
    MoodleAttempts.subscribeAll<MoodleAttempts>()(MoodleAttempts.allFields),
  );
  public rawGoals = new DataLoadingController(this, () => GoalAgg.subscribeAll<GoalAgg>()(GoalAgg.allFields));

  @state() public goalBuckets = new ObservableController(
    this,
    this.rawGoals.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const goalagg = raw.map(att => att.toJSON() as EntityData<GoalAgg>);
        // console.log('goalagg', goalagg);
        const distgoals = new Aggregator([{ $group: { _id: '$goalId' } }]).run(goalagg).map(x => x._id) as number[];
        const cellsByGoal = distgoals.reduce((a, c) => {
          return { ...a, [c]: '' };
        }, {});
        console.log('distgoals', distgoals);
        console.log('empty cellbygoals', cellsByGoal);

        for (const goal of distgoals) {
          const cells = new Aggregator([
            { $match: { goalId: goal } },
            { $set: { score: { $ifNull: ['$score', 0] } } }, // Set "not done" to a score of 0
            {
              $bucket: {
                groupBy: '$score',
                boundaries: [0, 0.24999999, 0.4999999, 0.7499999, 1.000001],
                default: 'Other',
                output: {
                  count: { $sum: 1 },
                  users: {
                    $push: {
                      userId: '$userId',
                      userName: '$userName',
                      pseudoRealname: '$pseudoRealname',
                      score: '$score',
                    },
                  },
                },
              },
            },
            { $match: { _id: { $ne: 'Other' } } },
          ]).run(goalagg);
          cellsByGoal[goal] = cells;
        }

        console.log('cells by goal-quartiles', cellsByGoal);

        const inverted = { 0: {}, 1: {}, 2: {}, 3: {} };
        for (const goal in cellsByGoal) {
          for (const quartile in cellsByGoal[goal]) {
            inverted[quartile][goal] = cellsByGoal[goal][quartile];
          }
        }
        return inverted;
      }),
      tap(x => console.log('cells by quartile-goals', x)),
    ),
  );

  @state() public goals = new ObservableController(
    this,
    this.rawGoals.observable.pipe(
      filter(Boolean),
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const attempts = raw.map(att => att.toJSON() as EntityData<GoalAgg>);
        const agg = new Aggregator([
          {
            $group: {
              _id: '$goalId',
              ...['goalTitle', 'goalText', 'goalLevel', 'taskIds', 'courseName', 'courseId', 'courseGroup'].reduce(
                (acc, field) => ({ ...acc, [field]: { $first: '$' + field } }),
                {},
              ),
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              },
              // avgscore: { $avg: '$score' },
            },
          },
        ])
          .run(attempts)
          .sort((a, b) => a._id - b._id) as Array<{
          _id: string;
          goalTitle: string;
          goalText: string;
          goalLevel: number;
          avgscore: number;
          taskIds: number[];
        }>;
        return agg;
      }),
    ),
  );

  @state() public updatedstate = new ObservableController(
    this,
    from(this.updateComplete).pipe(
      combineLatestWith(this.goalBuckets.observable, this.goals.observable),
      tap(([updatecomplete, goalBuckets, goals]) => {
        // console.log('chart main', this.chartMain, this.chartRef);
        try {
          if (!this.chartMain) this.initChartjs();
        } catch (e) {
          console.log('Chart existed already', e);
        }
        this.updateChart(goalBuckets, goals);
      }),
      throttleTime(3000),
    ),
  );

  render() {
    // Build "update chart data function reactively"
    // console.log('ACCORDEON', this.get_accordeon_items());
    // console.log('goals', this.goals.value);
    // console.log('goalbuckets', this.goalBuckets.value);
    // console.log('data', this.chartMain ? this.chartMain : '');
    return html`
      <style>
        ${microtip}
      </style>

      <x-flex space="sm" center crossaxis-center>
        <x-flex style="height:40px;"></x-flex>

        <x-h2 style="text-align:center; text-transform: capitalize;"
          >Leistungsverteilung aller SuS, unterteilt nach Lernzielen</x-h2
        >

        <!-- SECOND CARD -->
        <x-flex center crossaxis-center class="card" style="width:80vw; max-width:1200px">
          <canvas ${ref(x => (this.chartRef = x as HTMLCanvasElement))}></canvas>
          <x-flex
            horizontal
            space="sm"
            data-microtip-position="top"
            role="tooltip"
            data-microtip-size="large"
            aria-label="
            Wenn ein Schüler*innen keine Aufgaben erledigt hat, die zu einem bestimmten Lernziel zählen, wird seine Punktzahl zu diesem Lernziel mit 0 angesetzt.
            Die Punkte aller Schüler*innen werden für jedes einzelne Lernziel gezählt, dann
            gemittelt und in Quartile eingeteilt."
            crossaxis-center
          >
            <x-icon style="font-size: 18pt;">info</x-icon>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  public initChartjs() {
    Chart.defaults.font.size = 16;
    this.chartMain = new Chart(this.chartRef.getContext('2d')!, {
      type: 'bar',
      data: {
        labels: Array(11),
        datasets: [
          {
            label: 'Level 1 (0%-24%)',
            backgroundColor: colors.red,
            data: [],
          },
          {
            label: 'Level 2 (25%-49%)',
            backgroundColor: colors.orange,
            data: [],
          },
          {
            label: 'Level 3 (50%-74%)',
            backgroundColor: colors.yellow,
            data: [],
          },
          {
            label: 'Level 4 (75%-100%)',
            backgroundColor: colors.green,
            data: [],
          },
          // {
          //   label: 'Other',
          //   backgroundColor: colors.gray,
          //   data: [],
          // },
        ],
      },
      options: generateOptions('Lernziele', 'Anzahl SuS', { legend: true }),
    });

    if (this.chartMain.options.plugins) {
      console.log('GOALS', this.goals.value);
      this.chartMain.options.plugins.tooltip = {
        displayColors: false,
        callbacks: {
          title: ctx => {
            return this.goals.value?.find((g, i) => String(i) === String(ctx[0]?.dataIndex))?.goalTitle || 'ERROR';
          },
          label: function (ctx) {
            return ctx.dataset.label + ': ' + ctx.formattedValue + ' Schüler*innen';
          },
        },
      };
    }
    this.chartMain.options.onHover = (event, activeEvents) => {
      // console.log(event, activeEvents);
      (event?.native?.target as HTMLElement).style.cursor = activeEvents?.length > 0 ? 'pointer' : 'auto';
    };

    if (!this.chartRef) return;
    this.chartRef.onclick = (e: Event) => {
      if (!this.chartMain || !e) return;
      const points = this.chartMain.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true) as Array<{
        element: Element;
        datasetIndex: number;
        index: number;
      }>;
      if (!points.length) return;
      const goalId = points[0]!.index + 1;
      const goalSelected = Object.values(this.goals.value || {}).find(g => g._id == String(goalId));
      const quartBounds = [0, 0.25, 0.5, 0.75, 1];
      const quartile = points[0]!.datasetIndex;
      const percStart = quartBounds[quartile];
      const percEnd = quartBounds[quartile + 1];

      const selecteUsers = Object.entries(this.goalBuckets.value[quartile])[goalId - 1]?.[1];

      FeedbackPop.showPopup({
        props: {
          mode: 'percgoal',
          percStart: percStart,
          percEnd: percEnd,
          goalIdFilter: goalId,
          selectedUsers: selecteUsers,
          ...Object.fromEntries(Object.entries(goalSelected || {}).map(([i, v]) => ['popdata' + i, v])),
        },
      });
    };
  }

  public updateChart(goalBuckets, goals) {
    // Feed the data in as a series for every quartile
    if (!this.chartMain || !this.chartMain.data || !this.chartMain.data.datasets![0]) return;
    // logger.debug('UPDATE CHART', input);
    //this.chartMain.data.labels = Object.keys(input[0]).map(goal => 'Goal ' + goal);
    console.log('INPUT', goalBuckets);
    this.chartMain.data.labels = Object.values(goals.map(x => x.goalTitle));
    for (const quartile in goalBuckets) {
      this.chartMain.data.datasets![quartile].data = Object.values(goalBuckets[quartile]).map(goal => goal.count);
    }
    this.chartMain.update();
    // logger.debug('updated', this.chartMain.data.datasets);
  }
}
